import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { generateWithGemini } from "./ai/provider";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

const TLDS = [".com", ".net", ".io", ".sa", ".co", ".ai", ".org", ".app", ".dev", ".xyz", ".tech", ".store"];

// Wholesale base prices (USD). Markup is applied per provider config.
const PRICES: Record<string, { reg: number; renew: number }> = {
  ".com":   { reg: 8.50,  renew: 11.00 },
  ".net":   { reg: 10.50, renew: 13.00 },
  ".io":    { reg: 28.00, renew: 34.00 },
  ".sa":    { reg: 20.00, renew: 20.00 },
  ".co":    { reg: 21.50, renew: 26.00 },
  ".ai":    { reg: 60.00, renew: 70.00 },
  ".org":   { reg: 9.50,  renew: 12.00 },
  ".app":   { reg: 13.00, renew: 15.50 },
  ".dev":   { reg: 11.30, renew: 14.00 },
  ".xyz":   { reg: 1.50,  renew: 11.00 },
  ".tech":  { reg: 4.30,  renew: 43.00 },
  ".store": { reg: 3.30,  renew: 52.00 },
};

type ProviderId = "namesilo" | "namecheap" | "resellerclub";

type RegistrarConfig = {
  enabled: boolean;
  sandbox: boolean;
  markup_percent: number;
};

type RegistrarsSettings = {
  default_provider: ProviderId;
  providers: Record<ProviderId, RegistrarConfig>;
};

async function getActiveRegistrar(): Promise<{ provider: ProviderId | null; markup: number; sandbox: boolean }> {
  try {
    const { data } = await supabaseAdmin
      .from("site_settings")
      .select("value")
      .eq("key", "domain_registrars")
      .maybeSingle();
    const cfg = (data?.value as Partial<RegistrarsSettings>) || {};
    const defaultId = (cfg.default_provider || "namesilo") as ProviderId;
    const def = cfg.providers?.[defaultId];
    if (def?.enabled) {
      return { provider: defaultId, markup: Number(def.markup_percent) || 0, sandbox: !!def.sandbox };
    }
    // Fallback: first enabled provider
    for (const id of ["namesilo", "namecheap", "resellerclub"] as ProviderId[]) {
      const p = cfg.providers?.[id];
      if (p?.enabled) return { provider: id, markup: Number(p.markup_percent) || 0, sandbox: !!p.sandbox };
    }
  } catch {
    // ignore — fall through to defaults
  }
  return { provider: null, markup: 0, sandbox: false };
}

const inputSchema = z.object({
  query: z.string().min(1).max(63).regex(/^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)?$/, "Invalid characters"),
  tlds: z.array(z.string()).optional(),
});

export type DomainResult = {
  domain: string;
  tld: string;
  available: boolean | null; // null = unknown
  price?: number;
  renew?: number;
  premium?: boolean;
};

async function checkRdap(domain: string): Promise<boolean | null> {
  try {
    const ctl = new AbortController();
    const t = setTimeout(() => ctl.abort(), 4500);
    const res = await fetch(`https://rdap.org/domain/${domain}`, {
      headers: { Accept: "application/rdap+json" },
      signal: ctl.signal,
      redirect: "follow",
    });
    clearTimeout(t);
    if (res.status === 404) return true;   // not registered
    if (res.status === 200) return false;  // registered
    return null;                            // unknown / unsupported tld
  } catch {
    return null;
  }
}

function applyMarkup(amount: number, markupPct: number) {
  return Math.round(amount * (1 + markupPct / 100) * 100) / 100;
}

export const searchDomains = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => inputSchema.parse(input))
  .handler(async ({ data }) => {
    const raw = data.query.trim().toLowerCase();
    const hasTld = raw.includes(".");
    const sld = hasTld ? raw.split(".")[0] : raw;
    const requested = hasTld ? ["." + raw.split(".").slice(1).join(".")] : (data.tlds ?? TLDS);

    const list = requested
      .map((t) => (t.startsWith(".") ? t : "." + t))
      .filter((t, i, a) => a.indexOf(t) === i)
      .slice(0, 12);

    const registrar = await getActiveRegistrar();
    const markup = registrar.markup;

    const results = await Promise.all(
      list.map(async (tld): Promise<DomainResult> => {
        const domain = sld + tld;
        const available = await checkRdap(domain);
        const p = PRICES[tld];
        const premium = sld.length <= 3 && available === true;
        const baseReg = p ? (premium ? p.reg * 10 : p.reg) : undefined;
        return {
          domain,
          tld,
          available,
          price: baseReg !== undefined ? applyMarkup(baseReg, markup) : undefined,
          renew: p ? applyMarkup(p.renew, markup) : undefined,
          premium,
        };
      }),
    );

    results.sort((a, b) => {
      if (hasTld) return a.domain === raw ? -1 : b.domain === raw ? 1 : 0;
      if (a.tld === ".com") return -1;
      if (b.tld === ".com") return 1;
      const av = (x: DomainResult) => (x.available === true ? 0 : x.available === false ? 1 : 2);
      return av(a) - av(b);
    });

    return {
      query: sld,
      results,
      registrar: {
        provider: registrar.provider,
        markup_percent: markup,
        sandbox: registrar.sandbox,
      },
    };
  });

// =====================================================================
// AI-powered domain name research
// =====================================================================

const suggestSchema = z.object({
  brief: z.string().min(3).max(500),
  count: z.number().int().min(3).max(20).optional(),
  tlds: z.array(z.string().min(2).max(10)).max(8).optional(),
});

export type DomainSuggestion = {
  name: string;       // sld only (no TLD)
  tld: string;        // suggested TLD with leading dot
  rationale: string;  // short why
};

export const suggestDomainNames = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => suggestSchema.parse(input))
  .handler(async ({ data }) => {
    const count = data.count ?? 10;
    const tldHints = (data.tlds && data.tlds.length > 0
      ? data.tlds
      : [".com", ".io", ".ai", ".app", ".dev", ".co"]
    ).map((t) => (t.startsWith(".") ? t : "." + t));

    const system = [
      "You are a senior brand naming expert who suggests short, memorable, brandable domain names.",
      "Rules:",
      "- Names must be 3–18 chars, lowercase, letters/numbers/hyphen only, no leading/trailing hyphen.",
      "- Mix invented words, compound words, and metaphors. Avoid generic dictionary single-words when possible.",
      "- Pair each name with the TLD that fits best from the provided list.",
      "- Return STRICT JSON only — no prose, no markdown.",
    ].join("\n");

    const user = [
      `Brief: ${data.brief}`,
      `Allowed TLDs: ${tldHints.join(", ")}`,
      `Return exactly ${count} suggestions as JSON with shape:`,
      `{"suggestions":[{"name":"sldonly","tld":".com","rationale":"one short sentence"}]}`,
    ].join("\n");

    const content = await generateWithGemini({
      model: "gemini-3-flash-preview",
      system,
      messages: [{ role: "user", content: user }],
    });
    let parsed: { suggestions?: DomainSuggestion[] } = {};
    try {
      parsed = JSON.parse(content.replace(/^```(?:json)?\s*/i, "").replace(/\s*```$/i, "").trim());
    } catch {
      parsed = {};
    }

    const sanitize = (s: string) =>
      s.toLowerCase().replace(/[^a-z0-9-]/g, "").replace(/^-+|-+$/g, "").slice(0, 32);

    const allowedSet = new Set(tldHints);
    const suggestions: DomainSuggestion[] = (parsed.suggestions || [])
      .map((s) => {
        const name = sanitize(String(s?.name || ""));
        let tld = String(s?.tld || ".com").toLowerCase().trim();
        if (!tld.startsWith(".")) tld = "." + tld;
        if (!allowedSet.has(tld)) tld = tldHints[0];
        return { name, tld, rationale: String(s?.rationale || "").slice(0, 200) };
      })
      .filter((s) => s.name.length >= 3 && s.name.length <= 18)
      .slice(0, count);

    return { suggestions };
  });
